import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getSubjectById } from '../../config/subjects';
import { useAuth } from '../Auth/AuthContext';
import { fetchAuthSession } from '@aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function VideosView() {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const subject = getSubjectById(subjectId);

  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    if (!subject) {
      setError('Subject not found');
      setIsLoading(false);
      return;
    }

    fetchVideos();
  }, [subjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchVideos = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_BASE_URL}/api/videos?subjectId=${subjectId}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch videos');

      const data = await response.json();
      setVideos(data.videos || []);
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to fetch videos:', err);
      setError('Failed to load videos');
      setIsLoading(false);
    }
  };

  const handleVideoClick = (video) => {
    if (!isSubscribed) {
      navigate('/pricing');
      return;
    }
    setSelectedVideo(video);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-quicksand">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-study-teal mx-auto mb-4"></div>
          <p className="text-study-black text-xl">Loading videos...</p>
        </div>
      </div>
    );
  }

  if (!subject) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-quicksand">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-study-black mb-4">Subject Not Found</h1>
          <Link
            to="/academy"
            className="bg-study-teal text-study-black font-bold px-6 py-3 rounded-lg hover:bg-study-yellow transition-all"
          >
            Back to Essentials
          </Link>
        </div>
      </div>
    );
  }

  if (selectedVideo) {
    return (
      <VideoPlayer
        video={selectedVideo}
        subject={subject}
        onBack={() => setSelectedVideo(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white font-quicksand pb-12">
      {/* Header */}
      <div className="bg-gradient-to-r from-study-pink to-study-teal py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(`/academy/${subjectId}`)}
            className="flex items-center text-study-black hover:text-white transition-all mb-4"
          >
            <span className="text-2xl mr-2">←</span>
            <span className="font-bold">Back to {subject.name}</span>
          </button>
          <div className="flex items-center gap-4">
            <span className="text-5xl">{subject.icon}</span>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-study-black">
                {subject.name} Videos
              </h1>
              <p className="text-study-black text-lg">
                {videos.length} {videos.length === 1 ? 'video' : 'videos'} available
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 mt-6">
          <div className="bg-red-200 p-4 rounded-lg text-center">
            <p className="text-study-black text-lg font-bold">{error}</p>
          </div>
        </div>
      )}

      {/* Premium Warning for Free Users */}
      {!isSubscribed && videos.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 mt-6">
          <div className="bg-study-yellow p-6 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-study-black mb-2">Premium Content</h3>
            <p className="text-study-black mb-4">
              Videos are available exclusively for Premium members
            </p>
            <Link
              to="/pricing"
              className="inline-block bg-study-teal text-study-black font-bold px-6 py-3 rounded-lg hover:bg-study-pink transition-all"
            >
              Upgrade to Premium
            </Link>
          </div>
        </div>
      )}

      {/* Videos Grid */}
      <div className="max-w-7xl mx-auto px-4 mt-8">
        {videos.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-study-black text-xl mb-4">
              No videos available yet for {subject.name}
            </p>
            <p className="text-gray-600">Check back soon for new content!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <VideoCard
                key={video.videoId}
                video={video}
                isSubscribed={isSubscribed}
                onClick={() => handleVideoClick(video)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VideoCard({ video, isSubscribed, onClick }) {
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all cursor-pointer overflow-hidden group border-2 border-gray-100 hover:border-study-teal"
    >
      {/* Thumbnail placeholder */}
      <div className="bg-gradient-to-br from-study-teal to-study-pink h-40 flex items-center justify-center relative">
        <span className="text-6xl">▶️</span>
        {!isSubscribed && (
          <div className="absolute top-2 right-2 bg-study-yellow text-study-black text-xs font-bold px-2 py-1 rounded-full">
            Premium
          </div>
        )}
      </div>

      <div className="p-5">
        <h3 className="text-study-black font-bold text-lg mb-1">{video.title}</h3>
        {video.description && (
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">{video.description}</p>
        )}
        <div className="flex items-center justify-between">
          {video.duration && (
            <span className="text-gray-500 text-sm">{video.duration}</span>
          )}
          {!isSubscribed ? (
            <div className="bg-study-yellow text-study-black px-3 py-1 rounded-lg font-bold text-sm">
              Premium Only
            </div>
          ) : (
            <button className="bg-study-teal text-study-black font-semibold px-4 py-2 rounded-lg hover:bg-study-pink transition-all text-sm">
              Watch Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function VideoPlayer({ video, subject, onBack }) {
  return (
    <div className="min-h-screen bg-white font-quicksand pb-12">
      {/* Header */}
      <div className="bg-gradient-to-r from-study-pink to-study-teal py-6 px-6">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={onBack}
            className="flex items-center text-study-black hover:text-white transition-all mb-3"
          >
            <span className="text-2xl mr-2">←</span>
            <span className="font-bold">Back to Videos</span>
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-study-black">{video.title}</h1>
          <p className="text-study-black opacity-80">{subject.name}</p>
        </div>
      </div>

      {/* Video Player */}
      <div className="max-w-5xl mx-auto px-4 mt-8">
        <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl bg-black" style={{ paddingTop: '56.25%' }}>
          <iframe
            src={`https://player.vimeo.com/video/${video.vimeoId || video.vimeoID}?title=0&byline=0&portrait=0`}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={video.title}
          />
        </div>

        {/* Video Info */}
        {(video.description || video.duration) && (
          <div className="mt-6 bg-gray-50 rounded-xl p-6">
            {video.duration && (
              <p className="text-gray-500 text-sm mb-2">Duration: {video.duration}</p>
            )}
            {video.description && (
              <p className="text-study-black">{video.description}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default VideosView;
